import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapView } from "../components/MapView";
import { COUNTRY_NAMES, citiesOf, coordsOf, distanceKm } from "../lib/europe";
import { MapPin, Clock, CalendarBlank, LockKey, ShieldCheck, Lightning, NavigationArrow, X } from "@phosphor-icons/react";
import { secOptions, secLabelKey } from "../lib/securityTypes";

export default function Marketplace() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [country, setCountry] = useState("");
  const [distCity, setDistCity] = useState("");
  const [radius, setRadius] = useState(100);
  const [secType, setSecType] = useState("");
  const [loading, setLoading] = useState(true);
  const [geo, setGeo] = useState(null);       // [lat,lng] from the device GPS
  const [geoBusy, setGeoBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const p = {};
    if (country) p.country = country;
    api.get("/jobs", { params: p }).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); }, [country, user]);
  useEffect(() => { setDistCity(""); }, [country]);

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) { toast.error(t("geo_unsupported")); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo([pos.coords.latitude, pos.coords.longitude]); setGeoBusy(false); toast.success(t("geo_active")); },
      () => { setGeoBusy(false); toast.error(t("geo_denied")); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const cityOrigin = country && distCity ? coordsOf(country, distCity) : null;
  const origin = geo || cityOrigin;         // GPS wins; otherwise the chosen city
  const originLabel = geo ? t("my_location") : distCity;

  const filtered = useMemo(() => {
    let list = secType ? jobs.filter((j) => j.security_type === secType) : jobs;
    if (origin) {
      list = list
        .map((j) => ({ ...j, _dist: distanceKm(origin[0], origin[1], j.lat, j.lng) }))
        .filter((j) => j._dist <= radius)
        .sort((a, b) => a._dist - b._dist);
    }
    return list;
  }, [jobs, origin, radius, secType]);

  // Jobs are shielded: require authentication to browse
  if (ready && !user) {
    return (
      <div className="max-w-lg mx-auto px-5 py-24 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#E11D2A] flex items-center justify-center mx-auto">
          <LockKey weight="fill" className="text-white" size={30} />
        </div>
        <h1 data-testid="jobs-locked-title" className="font-head text-3xl font-bold tracking-tight mt-6">{t("jobs_locked_title")}</h1>
        <p className="text-zinc-500 mt-3">{t("jobs_locked_sub")}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <button data-testid="locked-login" onClick={() => navigate("/login")} className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-semibold rounded-md px-6 py-3 transition-colors">{t("nav_login")}</button>
          <button data-testid="locked-register" onClick={() => navigate("/register")} className="border border-zinc-300 hover:bg-zinc-50 font-semibold rounded-md px-6 py-3 transition-colors">{t("nav_register")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-8">
      <h1 className="font-head text-3xl font-bold tracking-tight">{t("nav_marketplace")}</h1>
      <p className="text-zinc-500 mt-1">{t("filter_hint")}</p>

      <div className="mt-6 bg-white border border-zinc-200 rounded-md p-4 grid md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{t("sec_type")}</label>
          <select data-testid="filter-security-type" value={secType} onChange={(e) => setSecType(e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E11D2A]">
            <option value="">{t("all_security")}</option>
            {secOptions(t).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{t("country")}</label>
          <select data-testid="filter-country" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E11D2A]">
            <option value="">{t("all_countries")}</option>
            {COUNTRY_NAMES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{t("distance")}</label>
          {geo ? (
            <div className="flex items-center gap-2 border border-[#E11D2A]/40 bg-[#E11D2A]/5 rounded-md px-3 py-2 text-sm">
              <NavigationArrow size={15} weight="fill" className="text-[#E11D2A]" />
              <span className="flex-1 font-medium text-zinc-700">{t("my_location")}</span>
              <button data-testid="filter-clear-location" onClick={() => setGeo(null)} className="text-zinc-400 hover:text-[#E11D2A]"><X size={15} weight="bold" /></button>
            </div>
          ) : (
            <div className="space-y-2">
              <select data-testid="filter-dist-city" value={distCity} onChange={(e) => setDistCity(e.target.value)} disabled={!country}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E11D2A] disabled:bg-zinc-100">
                <option value="">{t("choose_city")}</option>
                {citiesOf(country).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button data-testid="filter-use-location" onClick={useMyLocation} disabled={geoBusy}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold border border-[#E11D2A]/40 text-[#E11D2A] hover:bg-[#E11D2A]/5 rounded-md px-3 py-1.5 transition-colors disabled:opacity-60">
                <NavigationArrow size={13} weight="fill" />{geoBusy ? t("loading") : t("use_my_location")}
              </button>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
            {origin ? t("within_km").replace("{n}", radius) + " " + originLabel : t("any_distance")}
          </label>
          <input data-testid="filter-radius" type="range" min="10" max="500" step="10" value={radius} disabled={!origin}
            onChange={(e) => setRadius(parseInt(e.target.value))} className="w-full accent-[#E11D2A] disabled:opacity-40" />
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="space-y-3 order-2 lg:order-1">
          {loading && <p className="text-zinc-500">{t("loading")}</p>}
          {!loading && filtered.length === 0 && <p data-testid="no-jobs" className="text-zinc-500 py-10 text-center">{t("no_jobs")}</p>}

          {filtered.map((j) => <JobCard key={j.id} j={j} t={t} onClick={() => navigate(`/jobs/${j.id}`)} />)}
        </div>
        <div className="order-1 lg:order-2 lg:sticky lg:top-20 h-[360px] lg:h-[calc(100vh-8rem)]">
          <MapView markers={filtered} center={origin || [52.1, 5.29]} zoom={origin ? 8 : 5} onMarkerClick={(m) => navigate(`/jobs/${m.id}`)} />
        </div>
      </div>
    </div>
  );
}

const JobCard = ({ j, t, onClick }) => (
  <div data-testid={`job-card-${j.id}`} onClick={onClick}
    className="group relative bg-gradient-to-br from-[#151b26] to-[#0f141c] border border-white/5 border-l-4 border-l-[#E11D2A] rounded-r-md p-4 cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-l-[#FFCC00] hover:shadow-[0_12px_44px_-12px_rgba(0,47,167,0.55)]">
    <div className="flex justify-between items-start gap-3">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          {j.is_urgent && <span data-testid="spoed-badge" className="text-xs px-2 py-0.5 rounded-full font-bold bg-[#E11D2A] text-white flex items-center gap-1"><Lightning size={10} weight="fill" />{t("spoed_badge")}</span>}
          <h3 className="font-semibold text-lg">{j.title || (j.security_type ? t(secLabelKey(j.security_type)) : t("sec_generic"))}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30 flex items-center gap-1 shadow-[0_0_12px_-2px_rgba(255,204,0,0.4)]"><LockKey size={10} weight="fill" />€20</span>
        </div>
        {j.title && j.security_type && <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#E11D2A]"><ShieldCheck size={13} weight="fill" />{t(secLabelKey(j.security_type))}</span>}
      </div>
      <div className="text-right">
        <div className="font-head font-black text-xl text-[#E11D2A] tabular-nums">€{j.pay}</div>
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-4 text-sm text-zinc-600">
      <span className="flex items-center gap-1"><MapPin size={15} /> {j.location_name}{j.country ? `, ${j.country}` : ""}</span>
      <span className="flex items-center gap-1"><CalendarBlank size={15} /> {j.date}</span>
      <span className="flex items-center gap-1"><Clock size={15} /> {j.duration_hours}{t("hours")}</span>
      {j._dist != null && <span className="flex items-center gap-1 text-[#E11D2A] font-medium">{Math.round(j._dist)} km</span>}
    </div>
  </div>
);
